import React from 'react';
import {
  formatNumber, formatPercent, channelColor, channelLabel,
  STATUS_TONE, humanise,
} from '../hooks/useApi';

/* --------------------------------------------------------------- Card ----- */
export function Card({ title, note, actions, padded = true, children }) {
  return (
    <section className="spa-card">
      {(title || actions) && (
        <header className="spa-card__head">
          <div style={{ minWidth: 0 }}>
            {title && <h2 className="spa-card__title">{title}</h2>}
            {note && <p className="spa-card__note">{note}</p>}
          </div>
          {actions && <div style={{ marginLeft: 'auto' }}>{actions}</div>}
        </header>
      )}
      {padded ? <div className="spa-card__body">{children}</div> : children}
    </section>
  );
}

/* ------------------------------------------------------------- Metric ----- */
export function Metric({ label, value, change, suffix }) {
  const dir = change > 0.5 ? 'up' : change < -0.5 ? 'down' : 'flat';
  const arrow = dir === 'up' ? '↑' : dir === 'down' ? '↓' : '→';

  return (
    <div className="spa-metric">
      <div className="spa-metric__label">{label}</div>
      <div className="spa-metric__value">
        {typeof value === 'number' ? formatNumber(value) : value}
        {suffix}
      </div>
      {change != null && (
        <div className={`spa-metric__delta ${dir}`}>
          {arrow} {Math.abs(change).toFixed(1)}% <span>vs previous</span>
        </div>
      )}
    </div>
  );
}

/* --------------------------------------------------------------- Pill ----- */
export function StatusPill({ status }) {
  const tone = STATUS_TONE[status] || 'neutral';
  return <span className={`spa-pill spa-pill--${tone}`}>{humanise(status)}</span>;
}

export function ChannelTag({ channel }) {
  return (
    <span className="spa-chan" style={{ '--seg': channelColor(channel) }}>
      <span className="spa-chan__dot" aria-hidden="true" />
      {channelLabel(channel)}
    </span>
  );
}

/* ------------------------------------------------------------- States ----- */
export function EmptyState({ icon = 'fa-inbox', title, text, action }) {
  return (
    <div className="spa-empty">
      <i className={`fa-solid ${icon}`} aria-hidden="true" />
      <p className="spa-empty__title">{title}</p>
      {text && <p className="spa-empty__text">{text}</p>}
      {action && <div style={{ marginTop: 16 }}>{action}</div>}
    </div>
  );
}

export function ErrorState({ message, onRetry }) {
  return (
    <EmptyState
      icon="fa-triangle-exclamation"
      title="Could not load this"
      text={message}
      action={onRetry && (
        <button type="button" className="spa-btn" onClick={onRetry}>
          <i className="fa-solid fa-rotate-right" aria-hidden="true" /> Try again
        </button>
      )}
    />
  );
}

export function TableSkeleton({ rows = 6, cols = 5 }) {
  return (
    <div style={{ padding: 16 }}>
      {Array.from({ length: rows }).map((_, r) => (
        <div key={r} style={{ display: 'flex', gap: 12, marginBottom: 12 }}>
          {Array.from({ length: cols }).map((__, c) => (
            <div
              key={c}
              className="spa-skeleton"
              style={{ height: 16, flex: c === 0 ? 2 : 1 }}
            />
          ))}
        </div>
      ))}
    </div>
  );
}

/* --------------------------------------------------------- Date range ----- */
const RANGES = [
  { key: 'today', label: '24h' },
  { key: '7d', label: '7d' },
  { key: '30d', label: '30d' },
  { key: '90d', label: '90d' },
  { key: '12m', label: '12m' },
];

export function RangePicker({ value, onChange }) {
  return (
    <div className="spa-seg" role="group" aria-label="Date range">
      {RANGES.map((r) => (
        <button
          key={r.key}
          type="button"
          className={value === r.key ? 'is-on' : ''}
          aria-pressed={value === r.key}
          onClick={() => onChange(r.key)}
        >
          {r.label}
        </button>
      ))}
    </div>
  );
}

/* -------------------------------------------------------- Pagination ----- */
export function Pager({ pagination, onPage }) {
  if (!pagination) return null;
  const { page, pages, total, limit } = pagination;
  const first = total === 0 ? 0 : (page - 1) * limit + 1;
  const last = Math.min(page * limit, total);

  return (
    <div className="spa-pager">
      <span className="spa-pager__count">
        {formatNumber(first)}–{formatNumber(last)} of {formatNumber(total)}
      </span>
      <div className="spa-pager__nav">
        <button
          type="button" className="spa-btn spa-btn--sm"
          disabled={page <= 1} onClick={() => onPage(page - 1)}
        >
          Previous
        </button>
        <button
          type="button" className="spa-btn spa-btn--sm"
          disabled={page >= pages} onClick={() => onPage(page + 1)}
        >
          Next
        </button>
      </div>
    </div>
  );
}

/* ------------------------------------------------------------ Bar list ---- */
export function BarList({ items, valueKey = 'value', nameKey = 'name', colorBy }) {
  if (!items?.length) {
    return <EmptyState icon="fa-chart-simple" title="Nothing recorded yet"
      text="Data appears here once the site receives traffic in this period." />;
  }
  const max = Math.max(...items.map((i) => i[valueKey] || 0), 1);

  return (
    <ul className="spa-bars">
      {items.map((item) => (
        <li key={item[nameKey]}>
          <div className="spa-bars__top">
            <span className="spa-bars__name" title={item[nameKey]}>
              {item[nameKey] || 'Unknown'}
            </span>
            <span className="spa-bars__val">{formatNumber(item[valueKey])}</span>
          </div>
          <div className="spa-bars__track">
            <div
              className="spa-bars__fill"
              style={{
                width: `${(item[valueKey] / max) * 100}%`,
                background: colorBy ? channelColor(item[colorBy]) : undefined,
              }}
            />
          </div>
        </li>
      ))}
    </ul>
  );
}

/* --------------------------------------------------------------- Drawer --- */
export function Drawer({ open, onClose, title, subtitle, footer, children }) {
  React.useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [open, onClose]);

  if (!open) return null;

  return (
    <>
      <div className="spa-scrim" onClick={onClose} aria-hidden="true" />
      <aside className="spa-drawer" role="dialog" aria-modal="true" aria-label={title}>
        <header className="spa-drawer__head">
          <div style={{ minWidth: 0 }}>
            <h2 className="spa-drawer__title">{title}</h2>
            {subtitle && <p className="spa-card__note">{subtitle}</p>}
          </div>
          <button
            type="button" className="spa-btn spa-btn--sm"
            style={{ marginLeft: 'auto' }} onClick={onClose} aria-label="Close"
          >
            <i className="fa-solid fa-xmark" aria-hidden="true" />
          </button>
        </header>
        <div className="spa-drawer__body">{children}</div>
        {footer && <footer className="spa-drawer__foot">{footer}</footer>}
      </aside>
    </>
  );
}

export const Pct = ({ value }) => <>{formatPercent(value)}</>;
